import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Creators as sendValidationCodeActions } from '@/domain/useCases/profile/sendValidationCodeUseCase';
import { Creators as updateProfileActions } from '@/domain/useCases/profile/updateProfileUseCase';
import { Creators as updateDataProfileActions } from '@/domain/useCases/profile/updateDataProfileUseCase';
import { Creators as getProfileDataActions } from '@/domain/useCases/getProfileData';
import { Creators as getEmailAndPhoneActions } from '@/domain/useCases/getEmailAndPhoneUseCase';
import { Creators as validateCodeByTypeActions } from '@/domain/useCases/profile/validateCodeByTypeUseCase';
import { formatProfile, CapitalizeEachWord } from '../../helpers/format';
import PersonalInformationView from './PersonalInformationView';
import { type TActionState, initialActionState } from './types';
import GoogleAnalytics from '@/presentation/hooks/useGoogleAnalytics';
import { useRole } from '@/presentation/hooks/useRole';
import ModuleContainer from '@/presentation/components/Container';
import { SubsectionHeader } from '@/presentation/reusables/SectionHeader';

const PersonalInformationViewController = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { isVisor } = useRole();
  const [restriction, setRestriction] = useState(false);
  const { data: updateProfile, isLoading: isUpdatingData } = useSelector(
    (state: any) => state?.updateDataProfile
  );

  const profileContact = useSelector((state: any) => formatProfile(state));

  const { data: validateCodeStatus, isLoading: isValidatingCode } = useSelector(
    (state: any) => state.validateCodeByType
  );

  const profileData = useSelector((state: any) => state.getProfileData.data);

  const { data = {} } = useSelector((state: any) => state.profile);

  const { profile = {} } = data ?? {};
  const { firstName = '', lastName = '' } = profile ?? {};
  const fullName = {
    firstName: CapitalizeEachWord(firstName) ?? '',
    lastName: CapitalizeEachWord(lastName) ?? ''
  };

  const { isLoading: isUpdatingDataProfile, isSuccess: isSuccessDataProfile } =
    useSelector((state: any) => state?.updateProfile);

  const { data: sendCodeStatus, isLoading: isSendingCode } = useSelector(
    (state: any) => state.sendValidationCode
  );

  const { isLoading: isLoadingMasters } = useSelector(
    (state: any) => state?.getProfileMastersData
  );

  const [actionState, setActionState] =
    useState<TActionState>(initialActionState);

  const handlerBack = () => {
    history.push('/perfil');
  };

  const generateCode = () => {
    const code = [];
    const characters = '0123456789';

    for (let i = 0; i < 4; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      const randomDigit = characters.charAt(randomIndex);
      code.push(randomDigit);
    }

    return code.join('');
  };

  const controllerActionState = (state: Partial<TActionState>) => {
    if (state.code === 'GENERATE') {
      const code = generateCode();
      const email = actionState.input === 'EMAIL' ? actionState.newValue : '';
      const phone = actionState.input === 'PHONE' ? actionState.newValue : '';
      dispatch(sendValidationCodeActions.sendValidationCode(email, phone));
      state.code = code;
    }

    if (
      (state.input === 'EMAIL' || state.input === 'PHONE') &&
      !state?.requests
    ) {
      setActionState((prev) => ({ ...prev, ...state }));
      return;
    }

    if (state?.action === 'CONFIRM') {
      if (isVisor) {
        setRestriction(true);
        return;
      }
      dispatch(updateProfileActions.updateProfile(state.requests));
    }

    setActionState((prev) => ({ ...prev, ...state }));
  };

  useEffect(() => {
    GoogleAnalytics.writeLog(
      'portal-profile-renderPersonalInformation',
      'profile'
    );
    dispatch(getEmailAndPhoneActions.getEmailAndPhone());
    dispatch(getProfileDataActions.getProfileData());
  }, []);

  useEffect(() => {
    if (actionState.action === 'VALIDATION') {
      if (isVisor) {
        setRestriction(true);
        return;
      }
      if (updateProfile) {
        if (updateProfile?.status === 'success') {
          controllerActionState({ action: 'MESSAGE', status: 'SUCCESS' });
        } else {
          controllerActionState({ action: 'MESSAGE', status: 'ERROR' });
        }
      }
    }
  }, [updateProfile]);

  const handlerValidateCode = (validateCode: number) => {
    const email = actionState.input === 'EMAIL' ? actionState.newValue : '';
    const field = email ? 'email' : 'phone';
    if (isVisor) {
      setRestriction(true);
      return;
    }
    dispatch(validateCodeByTypeActions.validateCodeByType(validateCode, field));
  };

  useEffect(() => {
    if (!validateCodeStatus) return;
    if (validateCodeStatus?.status === 'OK') {
      controllerActionState({ status: 'SUCCESS' });
      dispatch(
        updateDataProfileActions.updateDataProfile(
          actionState.newValue,
          actionState.oldValue,
          actionState.input,
          actionState.code
        )
      );
      // setCountdownActive(false);
    } else {
      controllerActionState({ status: 'ERROR' });
    }
  }, [validateCodeStatus]);

  useEffect(() => {
    if (isSuccessDataProfile) {
      dispatch(getProfileDataActions.getProfileData());
      setActionState(initialActionState);
    }
  }, [isSuccessDataProfile]);

  const clearSendCodeHandler = () => {
    dispatch(sendValidationCodeActions.sendValidationCodeClear());
    controllerActionState({ action: 'DEFAULT' });
  };

  return (
    <ModuleContainer containerType="subsection">
      <SubsectionHeader
        title="Información personal"
        description="Revisa y agrega tus datos personales para seguir en contacto."
        onClick={handlerBack}
      />
      <PersonalInformationView
        actionState={actionState}
        controllerActionState={controllerActionState}
        profile={profileContact as any}
        onValidatingCode={handlerValidateCode}
        status={{
          sendCodeStatus,
          validateCodeStatus,
          updateProfile
        }}
        loaders={{
          isSendingCode,
          isValidatingCode,
          isUpdatingData,
          isUpdatingDataProfile
        }}
        onClearCode={clearSendCodeHandler}
        profileData={profileData}
        fullName={fullName}
        isLoadingMasters={isLoadingMasters}
        restrictionProps={{
          restriction,
          setRestriction
        }}
      />
    </ModuleContainer>
  );
};

export default PersonalInformationViewController;
